import { Injectable } from '@angular/core';
import { confetti } from '@tsparticles/confetti';
import { LoggingService } from '@app/core/services/logging/logging.service';

export interface ConfettiOptions {
  particleCount?: number;
  spread?: number;
  startVelocity?: number;
  decay?: number;
  gravity?: number;
  drift?: number;
  ticks?: number;
  origin?: {
    x?: number;
    y?: number;
  };
  colors?: string[];
  shapes?: string[];
  scalar?: number;
  zIndex?: number;
  disableForReducedMotion?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class ConfettiService {
  private defaultOptions: ConfettiOptions = {
    particleCount: 100,
    spread: 70,
    startVelocity: 30,
    decay: 0.95,
    gravity: 1,
    drift: 0,
    ticks: 200,
    origin: {
      x: 0.5,
      y: 0.7,
    },
    colors: [
      '#ff0000',
      '#00ff00',
      '#0000ff',
      '#ffff00',
      '#ff00ff',
      '#00ffff',
      '#ffa500',
      '#800080',
    ],
    scalar: 1,
    zIndex: 1000,
    disableForReducedMotion: true,
  };

  /**
   * Shows confetti animation with custom options
   * @param options Custom confetti options
   */
  async showConfetti(options: Partial<ConfettiOptions> = {}): Promise<void> {
    const mergedOptions = { ...this.defaultOptions, ...options };

    try {
      LoggingService.log('Showing confetti with options:', mergedOptions);

      // Use tsparticles confetti
      await confetti(mergedOptions);
      LoggingService.log('Confetti animation completed');
    } catch (error) {
      LoggingService.error('Error showing confetti:', error);
    }
  }

  /**
   * Shows confetti from a specific element
   * @param element The element to show confetti from
   * @param options Custom confetti options
   */
  async showConfettiFromElement(
    element: HTMLElement,
    options: Partial<ConfettiOptions> = {},
  ): Promise<void> {
    const rect = element.getBoundingClientRect();
    const origin = {
      x: (rect.left + rect.width / 2) / window.innerWidth,
      y: (rect.top + rect.height / 2) / window.innerHeight,
    };

    const mergedOptions = {
      ...this.defaultOptions,
      ...options,
      origin,
    };

    await this.showConfetti(mergedOptions);
  }

  /**
   * Shows confetti from the center of the screen
   * @param options Custom confetti options
   */
  async showConfettiFromCenter(
    options: Partial<ConfettiOptions> = {},
  ): Promise<void> {
    const mergedOptions = {
      ...this.defaultOptions,
      ...options,
      origin: { x: 0.5, y: 0.5 },
    };

    await this.showConfetti(mergedOptions);
  }

  /**
   * Shows confetti from the top of the screen
   * @param options Custom confetti options
   */
  async showConfettiFromTop(
    options: Partial<ConfettiOptions> = {},
  ): Promise<void> {
    const mergedOptions = {
      ...this.defaultOptions,
      ...options,
      origin: { x: 0.5, y: 0.1 },
    };

    await this.showConfetti(mergedOptions);
  }

  /**
   * Shows confetti with success theme colors
   * @param options Custom confetti options
   */
  async showSuccessConfetti(
    options: Partial<ConfettiOptions> = {},
  ): Promise<void> {
    const successColors = [
      '#4CAF50',
      '#8BC34A',
      '#CDDC39',
      '#FFEB3B',
      '#FFC107',
    ];

    const mergedOptions = {
      ...this.defaultOptions,
      ...options,
      colors: successColors,
    };

    await this.showConfetti(mergedOptions);
  }

  /**
   * Shows confetti with celebration theme colors
   * @param options Custom confetti options
   */
  async showCelebrationConfetti(
    options: Partial<ConfettiOptions> = {},
  ): Promise<void> {
    const celebrationColors = [
      '#FF6B6B',
      '#4ECDC4',
      '#45B7D1',
      '#96CEB4',
      '#FFEAA7',
      '#DDA0DD',
      '#98D8C8',
      '#F7DC6F',
      '#BB8FCE',
      '#85C1E9',
    ];

    const mergedOptions = {
      ...this.defaultOptions,
      ...options,
      colors: celebrationColors,
      particleCount: 150,
      spread: 90,
    };

    await this.showConfetti(mergedOptions);
  }

  /**
   * Shows confetti from multiple points for a dramatic effect
   * @param options Custom confetti options
   */
  async showMultiPointConfetti(
    options: Partial<ConfettiOptions> = {},
  ): Promise<void> {
    const baseOptions = {
      ...this.defaultOptions,
      ...options,
      particleCount: 50, // Reduced per point since we're showing multiple
    };

    // Show confetti from multiple points
    const promises = [
      this.showConfetti({ ...baseOptions, origin: { x: 0.2, y: 0.1 } }),
      this.showConfetti({ ...baseOptions, origin: { x: 0.8, y: 0.1 } }),
      this.showConfetti({ ...baseOptions, origin: { x: 0.5, y: 0.1 } }),
    ];

    await Promise.all(promises);
  }

  /**
   * Shows confetti in a wave pattern
   * @param options Custom confetti options
   */
  async showWaveConfetti(
    options: Partial<ConfettiOptions> = {},
  ): Promise<void> {
    const baseOptions = {
      ...this.defaultOptions,
      ...options,
      particleCount: 30,
    };

    // Show confetti in a wave pattern
    for (let i = 0; i < 5; i++) {
      setTimeout(async () => {
        await this.showConfetti({
          ...baseOptions,
          origin: { x: 0.1 + i * 0.2, y: 0.1 },
        });
      }, i * 100);
    }
  }
}
